import React, { FC, useRef, useEffect } from 'react';

import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { vwMSizes, vwDSizes } from 'utils/vwSizes';

import { useAppDispatch, useAppSelector, useResponsive, useGAEventCommonProps } from 'client/hooks';
import { deleteMessage, setUserMetadata } from 'client/redux/pubnub/actions';
import { setListenerHistory } from 'client/redux/room/actions';
import { MessageType, PubnubMsgsItem, UserMetadata, ParsedPubnubMessage } from 'client/redux/pubnub/types';
import { selectRoomId } from 'client/redux/room/selectors';
import { PB_CHANNELS } from 'client/redux/pubnub/constants';
import { analytics } from 'client/utils/analytics';

import ListenerIcon from '../../../RoomContent/ListenerIcon';
import ConnectionControls from '../../../ConnectionControls';
import Icon from '../../../Icon';

import css from './VirtualUsersList.module.scss';

type Props = {
  tabIndex: number;
  renderList: Array<PubnubMsgsItem> | Array<UserMetadata>;
  showMoreBtnState: (st: boolean) => void;
};

const listHeight = (listLength: number, size: number) => {
  return listLength * size + size / 2;
};

const VirtualUsersList: FC<Props> = (props) => {
  const { tabIndex, renderList, showMoreBtnState } = props;
  const dispatch = useAppDispatch();
  const { common } = useGAEventCommonProps();
  const roomId = useAppSelector(selectRoomId);
  const [isMobile] = useResponsive('MOBILE');
  const listRef = useRef<List>(null);
  const isRaiseHandOrBlockedTab = tabIndex === 0 || tabIndex === 2;

  const removeFromBlockedList = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    const { id, timetoken } = e.currentTarget.dataset;
    console.info('Remove this user from Blocked List = ', id, timetoken);

    if (timetoken && id) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: 'Unblock user',
      });

      // update user metadata
      dispatch(
        setUserMetadata({
          uuid: `${roomId}.${PB_CHANNELS.USERS}.${id}`,
          data: { externalId: '' },
        }),
      );
      // Set listener unblocked status in history
      dispatch(setListenerHistory({ uid: id, roomId: roomId, isBlocked: false }));
      //remove message from blocked list
      dispatch(
        deleteMessage({
          channel: `${roomId}.${PB_CHANNELS.BLOCKED}`,
          timeToken: timetoken,
          messageType: MessageType.BLOCK,
        }),
      );
    }
  };

  const renderItemFromMetadata = (l: UserMetadata) => {
    return (
      <ListenerIcon
        name={l.name || ''}
        timesSpoke={l.email || ''}
        className={css.listenerAvatar}
        classNameOfName={css.listenerName}
      />
    );
  };

  const renderItemFromMessage = (l: PubnubMsgsItem) => {
    const data: ParsedPubnubMessage = JSON.parse(l.message || '');

    const parsedMessage: ParsedPubnubMessage = JSON.parse(l.message);
    const actualMsgData = Object.keys(l.actions?.update || {});
    const actualMsgDataParsed: ParsedPubnubMessage = JSON.parse(actualMsgData?.[0] || '{}');

    switch (tabIndex) {
      case 0: // waiting list
        const listener = {
          id: data.id || (l.uuid as string),
          name: data.name,
          messageTimeToken: l.timetoken,
          alreadySpeakTotal: actualMsgDataParsed.alreadySpeakTotal || parsedMessage.alreadySpeakTotal,
        };

        return (
          <>
            <ListenerIcon
              name={data.name}
              className={css.listenerAvatar}
              classNameOfName={css.listenerName}
              wantToSpeak
              timesSpoke={listener.alreadySpeakTotal}
            />
            <ConnectionControls listener={listener} />
          </>
        );
      case 2: // blocked
        return (
          <>
            <ListenerIcon name={data.name} className={css.listenerAvatar} classNameOfName={css.listenerName} />
            <button
              type="button"
              data-id={data.id || l.uuid}
              data-timetoken={l.timetoken}
              onClick={removeFromBlockedList}
            >
              <Icon type="rounded-checked" className={css.unblockIcon} />
            </button>
          </>
        );

      default:
        return null;
    }
  };

  const renderListItem = (index: number, style: React.CSSProperties) => {
    if (renderList[index]) {
      const totalInfo = renderList[index] as PubnubMsgsItem;
      const userInfo = isRaiseHandOrBlockedTab ? JSON.parse(totalInfo.message || '{}') : totalInfo;

      return (
        <div key={userInfo.id} className={css.listItem} style={style}>
          {isRaiseHandOrBlockedTab ? renderItemFromMessage(totalInfo) : renderItemFromMetadata(userInfo)}
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    listRef.current?.scrollToItem(0);
  }, [tabIndex]);

  return (
    <AutoSizer>
      {({ width: w, height: h }: { width: number; height: number }) => {
        const size = isMobile ? vwMSizes(71) : vwDSizes(71);
        const width = w || 0;
        const height = h || 0;

        return (
          <>
            <List
              ref={listRef}
              height={height}
              itemCount={renderList.length}
              itemSize={size}
              width={width}
              direction="rtl"
              className={css.list}
              onScroll={(scroll) => {
                if (scroll.scrollOffset + height === listHeight(renderList.length, size)) {
                  showMoreBtnState(true);
                } else {
                  showMoreBtnState(false);
                }
              }}
            >
              {({ index, style }) => renderListItem(index, style)}
            </List>
          </>
        );
      }}
    </AutoSizer>
  );
};

export default VirtualUsersList;
